import { Alert, Box, Typography } from "@mui/material";
import {
	BooleanInput,
	DeleteButton,
	Edit,
	email,
	required,
	SaveButton,
	SelectInput,
	SimpleForm,
	TextInput,
	Toolbar,
	usePermissions,
	useRecordContext,
} from "react-admin";
import { RestoreButton } from "@/components/RestoreButton";
import type { UserRole } from "@/providers/authProvider";
import { BanButton } from "./BanButton";

const UserEditToolbar = () => {
	const record = useRecordContext();

	return (
		<Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
			<SaveButton />
			<Box sx={{ display: "flex", gap: 1 }}>
				<BanButton />
				{record?.deletedAt && <RestoreButton resource="users" />}
				<DeleteButton />
			</Box>
		</Toolbar>
	);
};

const DeletedAlert = () => {
	const record = useRecordContext();
	if (!record?.deletedAt) return null;

	return (
		<Alert severity="warning" sx={{ mb: 2 }}>
			This user was deleted on {new Date(record.deletedAt).toLocaleDateString()}
			. You can restore them using the Restore button below.
		</Alert>
	);
};

const BannedAlert = () => {
	const record = useRecordContext();
	if (!record?.banned) return null;

	return (
		<Alert severity="error" sx={{ mb: 2 }}>
			This user is banned.
			{record.banReason && (
				<Typography variant="body2">Reason: {record.banReason}</Typography>
			)}
			{record.banExpires && (
				<Typography variant="body2">
					Expires: {new Date(record.banExpires).toLocaleString()}
				</Typography>
			)}
		</Alert>
	);
};

export const UserEdit = () => {
	const { permissions } = usePermissions<{ role: UserRole }>();
	const isSuperAdmin = permissions?.role === "super_admin";

	return (
		<Edit>
			<DeletedAlert />
			<BannedAlert />
			<SimpleForm toolbar={<UserEditToolbar />}>
				<TextInput source="id" disabled />
				<TextInput source="name" validate={required()} fullWidth />
				<TextInput source="email" validate={[required(), email()]} fullWidth />
				<BooleanInput source="emailVerified" label="Email Verified" />
				<SelectInput
					source="role"
					choices={[
						{ id: "user", name: "User" },
						{ id: "admin", name: "Admin" },
						{ id: "super_admin", name: "Super Admin" },
					]}
					validate={required()}
					disabled={!isSuperAdmin}
					helperText={
						!isSuperAdmin ? "Only super admins can change roles" : undefined
					}
				/>
				<TextInput source="image" label="Avatar URL" fullWidth />
				<TextInput
					source="stripeCustomerId"
					disabled
					label="Stripe Customer ID"
				/>
			</SimpleForm>
		</Edit>
	);
};
